import Cookies from 'js-cookie';
import { loadScript } from './utilities';
import tags from './tags';

const { country, countryRegion } = tags;

// array of GDPR countries.
const gdprMembers = [
  'AT',
  'BE',
  'BG',
  'BR',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'LI',
  'IS',
  'NO',
];

// array of CCPA states
const ccpaMembers = ['CA', 'California', 'CO', 'Colorado', 'VA', 'Virginia'];

/**
 * Determines if GDPR should apply
 *
 * @param countryCode
 * @returns {boolean}
 */
const isGdpr = (countryCode) => {
  const url = document.URL;
  if (url.includes('debug=gdpr')) return true;

  return gdprMembers.includes(countryCode);
};

/**
 * Determines if CCPA should apply
 *
 * @param countryCode
 * @param countryRegion
 * @returns {boolean}
 */
const isCcpa = (countryCode, countryRegion) => {
  const url = document.URL;
  if (url.includes('debug=ccpa')) return true;

  return 'US' === countryCode && ccpaMembers.includes(countryRegion);
};

/**
 * CMP Class
 */
class Cmp {
  // System1 CMP version to load
  version = '2.2.2';

  // SYstem1 CMP URL
  url = `https://s.flocdn.com/cmp/${this.version}/tcf-2.0-loader.js`;

  // This will be a queue of functions to run.
  queue = [];

  // This is variable that will ensure that runQueue only runs once.
  queue_initialized = false;

  /**
   * This will run when the CMP class is instantiated.
   */
  constructor() {
    // (boolean) true if GDPR should apply
    this.gdprApplies = isGdpr(country);

    // (boolean) true if CCPA should apply
    this.ccpaApplies = isCcpa(country, countryRegion);

    if (this.gdprApplies) {
      this.loadCmp();

      // Run the CMP queue if the gdpr_opt_in cookie is present..
      if (Cookies.get('gdpr_opt_in')) {
        if (Cookies.get('gdpr_opt_in') === '1') {
          this.runQueue(1);
        } else {
          this.runQueue(0);
        }
      }
    } else if (this.ccpaApplies) {
      // IAB's Reference UspAPI implementation (https://github.com/InteractiveAdvertisingBureau/CCPA-reference-code)
      loadScript('/wp-content/mu-plugins/cm-privacy-enabler/js/uspapi.js');

      // Set the usprivacy cookie if doesn't exist already.
      if (!Cookies.get('usprivacy')) {
        this.setUspApiCookie();
      }

      // Run the CMP queue.
      if (document.cookie.indexOf('gdpr_opt_in=0') > -1) {
        this.runQueue(0); // consent failed
      } else {
        this.runQueue(1);
      }
    } else {
      // not GDPR or CCPA. run the queue right away.
      this.runQueue(1);
    }

    window.s1 = window.s1 || {};
    window.s1.CmpWrapper = this;
  }

  /**
   * Initializes the CMP
   */
  loadCmp() {
    loadScript(this.url, () => {
      const config = {
        gdprApplies: this.gdprApplies,
        ccpaApplies: this.ccpaApplies,
        // shouldAutoShowModal: !this.ccpaApplies,
        //		logging: true, // console logs
        baseUrl: `https://s.flocdn.com/cmp/${this.version}/config/2.0`, // base url for vendor-lists
        //		versionedFilename: 'vendor-list.json', // vendor list json
        scriptSrc: `https://s.flocdn.com/cmp/${this.version}/tcf-2.0-cmp.js`, // cmp SDK
        business: 'publishing',
        canLog: true,
        experimentId: 'cmd-cmp2',
        //		publisherCountryCode: 'AA',
        //		narrowedVendors: [], // ex [1,2,3,4],
        theme: {
          boxShadow: 'none',
          shouldAutoResizeModal: true,
          primaryColor: '#0099ff',
          isFullWidth: true,
        },
        shouldUseStacks: true,
        isSlimMode: true,
        shouldShowCloseX: false,
        shouldAlwaysShowSaveButton: true,
      };

      const onCmpLoaded = (result) => {
        if (document.cookie.indexOf('gdpr_opt_in=1') < 0) {
          console.log('all:consent:failed', result);
          // window.location.reload();
          this.runQueue(0);
        } else {
          console.log('all:consent:succeeded', result);
          // window.dataLayer.push({ event: 'cookieconsented' });
          // window.dataLayer.push({ event: 'cookieconsent_preferences' });
          // window.dataLayer.push({ event: 'cookieconsent_statistics' });
          // window.dataLayer.push({ event: 'cookieconsent_marketing' });
          this.runQueue(1);
        }
      };

      const onConsentChanged = (result) => {
        console.log('consent changed');

        if (this.ccpaApplies) {
          this.setUspApiCookie(); // update usprivacy cookie.
        }

        // force reload.
        window.location.reload();

        // onCmpLoaded(result);
      };

      window.__tcfapi(
        'init',
        2,
        (store, error) => {
          if (store.isModalShowing) {
            // if the banner is showing (first pageview)
            // __tcfapi('onConsentAllChanged', 2, onConsentChanged);
          } else {
            // subsequent pageviews
            onCmpLoaded(store);
          }
          console.log('initialization complete', store, error);
        },
        config
      );

      // Listen to consent changes.
      window.__tcfapi('onConsentAllChanged', 2, onConsentChanged);
    });
  }

  /**
   * Enqueue functions that will wait for the CMP consent to be set. Enqueue takes in 2 arguments,
   * The function to run and the consent level when the function can run.
   *
   * @param function_call the function to run
   * @param consent_level the consent level when to run the function. 1 is full consent and 0 is no consent.
   */
  enqueue(function_call, consent_level = 1) {
    this.queue.push([function_call, consent_level]);
  }

  /**
   * Initializes the queue and run all the functions whose consent level matches.
   * This method can only be run once.
   *
   * @param consent_level the consent level when to run the function. 1 is full consent and 0 is no consent.
   */
  runQueue(consent_level = 1) {
    if (!this.queue_initialized) {
      console.log('CMP queue starting');
      this.queue_initialized = true;
    } else {
      console.log('CMP queue already intialized');
      return;
    }

    // excecute the queue.
    this.queue.forEach((element) => {
      const [f, consent] = element;
      if (consent == consent_level) {
        f();
      }
    });

    // run all subsequent functions that are added to the queue directly.
    this.queue._push = this.queue.push;
    this.queue.push = (element) => {
      const [f, consent] = element;
      if (!!(f && f.constructor && f.call && f.apply) && consent == consent_level) {
        this.queue._push(f);
        f();
      }
    };
  }

  /**
   * Will trigger the Banner for the respective Privacy Policy.
   */
  showCmpBanner() {
    if (this.gdprApplies) {
      window.__tcfapi('showConsentTool', 2, () => {});
    } else {
      if (window.__tcfapi) {
        /**
         * if that variable is present, the CMP has already been initialized.
         * We can only run the __tcfapi function to show the CMP banner.
         */
        console.log('second load');
        window.__tcfapi('showConsentTool', 2, () => {});
      } else {
        console.log('first load');
        if (Cookies.get('euconsent-v2')) {
          /**
           * Check if euconsent-v2 cookie exists. We need to do that because if that cookie already exists,
           * the CMP banner will not automatically show. We have to manually trigger the CMP banner to show.
           */
          window.setTimeout(() => {
            window.__tcfapi('showConsentTool', 2, () => {});
          }, 1000);
        } else {
          /**
           * If euconsent-v2 does not exist, we set the cookie as opted in when the CMP initializes,
           * so that all the toggles are on. This cookie will get overwritten when the user interacts with the CMP.
           */
          Cookies.set(
            'euconsent-v2',
            'CPOwGzDPOwGzDAmAGBENBtCsAP_AAH_AACiQIOtf_X__bX9j-_59f_t0eY1P9_r_v-Qzjhfdt-8F2L_W_L0X42E7NF36pq4KuR4Eu3LBIQNlHMHUTUmwaokVrzHsak2MryNKJ7LEmnMZO2dYGHtPn91TuZKY7_78__fz3z-v_t_-39T37-3_3__5_X---_e_V399zLv9____39nN___9v-CDYBJhqXkAXYljgybRpVCiBGFYSHQCgAooBhaJrABgcFOysAj1BCwAQmoCMCIEGIKMGAQACAQBIREBIAWCARAEQCAAEAKkBCAAiYBBYAWBgEAAoBoWIEUAQgSEGRwVHKYEBEi0UEtlYAlF3saYQhllgBQKP6KjARKEECwMhIWDmOAJAS4WAAAA.YAAAAAAAAAAA'
          );
          /**
           * Because we are setting the cookie above, the CMP banner will not show. We have to trigger it manually.
           */
          window.setTimeout(() => {
            window.__tcfapi('showConsentTool', 2, () => {});
          }, 1000);
        }
        this.loadCmp(); // initialize the CMP
      }
    }
  }

  /**
   * Create the usprivacy cookie base on gdpr_opt_in cookie, or update the cookie if it already exists.
   */
  setUspApiCookie() {
    let usprivacy = '1---'; // CCPA does not apply.
    if (this.ccpaApplies) {
      const consent = Cookies.get('gdpr_opt_in');
      if (consent === '0') {
        usprivacy = '1YY-'; // Has opted out
      } else {
        usprivacy = '1YN-'; // Hasn't opted out.
      }
    }
    Cookies.set('usprivacy', usprivacy);
    console.log(`Setting usprivacy cookie to ${usprivacy}.`);
  }
}

// export the CMP class as a singleton (can only be instantiated once).
export default new Cmp();
